import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Progress, Space, Tag, Typography, Spin,
} from 'antd';
import { CloudDownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { historicoDescargasService } from '../../services/api';
import ResponsiveTable from '../../components/Common/ResponsiveTable';

const { Text, Paragraph } = Typography;

const ESTADOS = {
  descargada: { color: 'green', label: 'Descargada' },
  a_medias: { color: 'orange', label: 'A medias' },
  en_curso: { color: 'blue', label: 'En curso' },
  sin_descargar: { color: 'default', label: 'Sin descargar' },
};

/**
 * Histórico de descargas del SAT: 24 meses en 8 ventanas de tres meses.
 *
 * Sirve para conciliar IVA acreditable contra IVA a pagar de dos años, que exige
 * que esos documentos estén descargados en SW. La programación diaria solo mira
 * hacia adelante, así que la historia hay que pedirla.
 *
 * UNA VENTANA POR PETICIÓN. El histórico completo son hasta 17 llamadas a SW y en
 * una sola petición se rompería a mitad, dejando parte solicitada y parte no. Aquí
 * se recorren las que falten, con avance visible y opción de detener.
 *
 * DICE "SOLICITADO", NO "DESCARGADO". SW resuelve las descargas por su cuenta y
 * tarda; decir lo segundo invitaría a conciliar sobre documentos que todavía no
 * han llegado, y eso no da ningún error: da un número.
 */
const HistoricoDescargasPanel = ({ empresa }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const [corriendo, setCorriendo] = useState(false);
  const [detener, setDetener] = useState(false);
  const detenerRef = useRef(false);
  const [hechas, setHechas] = useState(0);
  const [porHacer, setPorHacer] = useState(0);
  const [enCurso, setEnCurso] = useState(null);
  const [solicitadas, setSolicitadas] = useState(0);
  const [fallos, setFallos] = useState([]);
  const [termino, setTermino] = useState(false);

  useEffect(() => {
    detenerRef.current = detener;
  }, [detener]);

  const cargar = useCallback(async () => {
    if (!empresa?.id) return;
    setCargando(true);
    setError('');

    try {
      setDatos(await historicoDescargasService.getEstado(empresa.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo consultar el histórico');
    } finally {
      setCargando(false);
    }
  }, [empresa?.id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const pendientes = (datos?.ventanas || []).filter((v) => v.estado !== 'descargada');

  const solicitar = async () => {
    const cola = pendientes;
    if (cola.length === 0) return;

    setCorriendo(true);
    setDetener(false);
    detenerRef.current = false;
    setTermino(false);
    setHechas(0);
    setPorHacer(cola.length);
    setSolicitadas(0);
    setFallos([]);

    let creadas = 0;
    const errores = [];

    for (let i = 0; i < cola.length; i++) {
      if (detenerRef.current) break;

      const ventana = cola[i];
      setEnCurso(ventana);

      try {
        // eslint-disable-next-line no-await-in-loop
        const salida = await historicoDescargasService.solicitarVentana(empresa.id, ventana.clave);

        if (salida?.ok) {
          creadas += contarSolicitudes(salida);
        } else {
          errores.push({ clave: ventana.etiqueta, motivo: salida?.motivo || 'No se pudo solicitar' });
        }
      } catch (err) {
        const cuerpo = err?.response?.data;
        errores.push({
          clave: ventana.etiqueta,
          motivo: cuerpo?.motivo || cuerpo?.error || 'No se pudo solicitar',
        });
      }

      setSolicitadas(creadas);
      setFallos([...errores]);
      setHechas(i + 1);
    }

    setEnCurso(null);
    setCorriendo(false);
    setTermino(true);
    await cargar();
  };

  const contarSolicitudes = (salida) => {
    let n = 0;
    ['emitidos', 'recibidos'].forEach((tipo) => {
      (salida?.[tipo]?.tramos || []).forEach((tramo) => {
        if (tramo.ok || tramo.attempts) n += 1;
      });
    });
    return n;
  };

  const columnas = [
    { title: 'Trimestre', dataIndex: 'etiqueta', key: 'etiqueta' },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const meta = ESTADOS[estado];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : <Tag>Desconocido</Tag>;
      },
    },
    {
      title: 'Emitidos',
      dataIndex: 'emitidos',
      key: 'emitidos',
      render: (v) => <Text type="secondary">{ESTADOS[v]?.label || '—'}</Text>,
    },
    {
      title: 'Recibidos',
      dataIndex: 'recibidos',
      key: 'recibidos',
      render: (v) => <Text type="secondary">{ESTADOS[v]?.label || '—'}</Text>,
    },
  ];

  if (cargando && !datos) {
    return <div><Spin /> <Text type="secondary">Consultando el histórico…</Text></div>;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {!!error && <Alert type="error" showIcon message={error} />}

      {/* El problema de e.firma se advierte aquí, para no tener que descubrirlo
          al intentar: sin ella las 16 solicitudes fallarían una por una. */}
      {!!datos?.problema_efirma && (
        <Alert
          type="warning"
          showIcon
          message="No se puede solicitar el histórico"
          description={datos.problema_efirma}
        />
      )}

      {datos?.completo && (
        <Alert
          type="success"
          showIcon
          message="El histórico de 24 meses ya está descargado"
          description="No hay nada que solicitar."
        />
      )}

      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Dos años en 8 trimestres. Cada trimestre se pide por separado y solo se solicita lo que
        falta, así que repetirlo no cuesta nada.
      </Paragraph>

      <ResponsiveTable
        rowKey="clave"
        loading={cargando}
        dataSource={datos?.ventanas || []}
        columns={columnas}
        pagination={false}
        size="small"
      />

      {corriendo && (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Progress percent={porHacer ? Math.round((hechas / porHacer) * 100) : 0} />
          <Text>{hechas} / {porHacer} trimestres</Text>
          {enCurso && <Text type="secondary">Solicitando {enCurso.etiqueta}…</Text>}
        </Space>
      )}

      {termino && (
        <Alert
          type={fallos.length > 0 ? 'warning' : 'info'}
          showIcon
          message={`${solicitadas} descarga${solicitadas === 1 ? '' : 's'} solicitada${solicitadas === 1 ? '' : 's'}`}
          description={
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {/* Solicitado, no descargado: SW tarda en resolverlas. */}
              <Text>Las descargas están en curso en SW y tardan un rato en completarse.</Text>
              {fallos.map((f) => (
                <Text key={f.clave} type="secondary" style={{ fontSize: 12 }}>
                  {f.clave}: {f.motivo}
                </Text>
              ))}
            </Space>
          }
        />
      )}

      <Space>
        <Button
          type="primary"
          icon={<CloudDownloadOutlined />}
          loading={corriendo}
          disabled={corriendo || !!datos?.problema_efirma || pendientes.length === 0}
          onClick={solicitar}
        >
          {pendientes.length > 0
            ? `Solicitar ${pendientes.length} trimestre${pendientes.length === 1 ? '' : 's'}`
            : 'Nada que solicitar'}
        </Button>

        {corriendo && <Button danger onClick={() => setDetener(true)}>Detener</Button>}

        {!corriendo && (
          <Button icon={<ReloadOutlined />} disabled={cargando} onClick={cargar}>
            Actualizar
          </Button>
        )}
      </Space>
    </Space>
  );
};

export default HistoricoDescargasPanel;
